import { fallbackOf } from "../settings/catalog";
import { useText } from "../settings/context";

/**
 * THE ORGANISER'S OWN WORDS FOR THE FUNNEL'S NOTIONS.
 *
 * One event calls it a typology, the next a profile, a category, a status. The
 * product's word is "typologie" — it is the column, and what support quotes — but
 * the SITE must speak the client's, and speak it in every place at once.
 *
 * ⚠️ ONE SETTING PER NOTION, read from here by every screen that names it. Written
 * where it is displayed, the same notion ends up with two words on two screens: the
 * typology step already said "Aucun profil proposé" and, one line below,
 * "Réactivez au moins une typologie".
 *
 * The step's NAME is that word: it is the noun in the breadcrumb ("Typologie"),
 * while the step's TITLE is the question asked ("Vous êtes…"). Two settings, two
 * jobs — and it is the noun that belongs here.
 */

const TYPOLOGY = "FO_MEETING_V2_TYPO_STEP_LABEL";
const PARTICIPATION = "FO_MEETING_V2_REGISTRATION_STEP_LABEL";
const COMPANY = "FO_MEETING_V2_COMPANY_STEP_LABEL";
const DATES = "FO_MEETING_V2_DATES_STEP_LABEL";
const CONFERENCES = "FO_MEETING_V2_CONFERENCES_STEP_LABEL";
const ARTICLES = "FO_MEETING_V2_ARTICLES_STEP_LABEL";

/**
 * ⚠️ `useWord` ET NON `word` : il APPELLE UN HOOK, donc il en est un, et son nom doit le
 * dire. Sous son ancien nom, rien n'empêchait un appelant de le glisser dans une
 * condition ou une boucle — ce que React interdit et qu'aucun outil ne relève quand le
 * nom ne commence pas par `use`. Il se trouve que les six appels ci-dessous sont
 * inconditionnels ; c'est la seule raison pour laquelle rien n'avait cassé.
 */
function useWord(setting: string): string {
  const written = useText(setting, "");
  return written.trim() ? written : fallbackOf(setting);
}

/** What this event calls a participant's profile. */
export function useTypologyWord(): string {
  return useWord(TYPOLOGY);
}

/** What this event calls the way somebody takes part. */
export function useParticipationWord(): string {
  return useWord(PARTICIPATION);
}

/**
 * Ce que cet événement appelle la maison qu'on représente. « Société » chez la
 * plupart, « Entreprise » ou « Exposant » ailleurs — et le récapitulatif doit le dire
 * comme le fil d'Ariane, sans quoi la même chose y porte deux noms à deux écrans.
 */
export function useCompanyWord(): string {
  return useWord(COMPANY);
}

/** Ce que cet événement appelle les jours de présence. */
export function useDatesWord(): string {
  return useWord(DATES);
}

/** Ce que cet événement appelle son programme : conférences, ateliers, sessions… */
export function useConferencesWord(): string {
  return useWord(CONFERENCES);
}

/** Ce que cet événement appelle ce qui se vend : billetterie, pass, options… */
export function useArticlesWord(): string {
  return useWord(ARTICLES);
}
